import { fetchApi } from '../client';
import i18n from '../../../i18n';
import type { PagedResponse } from '../../../types/pagination';

export type ReferralSource =
  | 'walk_in'
  | 'existing_patient'
  | 'google'
  | 'social_media'
  | 'doctor_referral'
  | 'advertisement'
  | 'other';

export type RewardType = 'NONE' | 'DISCOUNT' | 'FREE_CONSULTATION' | 'CREDIT';
export type ReferralStatus = 'PENDING' | 'CONVERTED' | 'REWARDED' | 'CANCELLED';

export interface PatientReferral {
  id: string;
  clinicId: string;
  referredPatientId: string;
  referrerPatientId?: string;
  /** Names filled in by the server for the lists (null when the patient is not on this clinic's records). */
  referredPatientName?: string | null;
  referrerPatientName?: string | null;
  referralSource: ReferralSource;
  rewardType: RewardType;
  rewardValue: number;
  rewardApplied: boolean;
  rewardAppliedAt?: string;
  status: ReferralStatus;
  createdAt: string;
  updatedAt: string;
}

export interface ReferralStats {
  total: number;
  converted: number;
  conversionRate: number;
  bySource: Record<string, number>;
  topReferrerCount: number;
}

const REFERRAL_SOURCE_ENGLISH: Record<ReferralSource, string> = {
  walk_in:          'Walk-in',
  existing_patient: 'Referred by Patient',
  google:           'Google Search',
  social_media:     'Social Media',
  doctor_referral:  'Doctor Referral',
  advertisement:    'Advertisement',
  other:            'Other',
};

/**
 * Display labels per source. Each value is looked up in `enums:referralSource.<code>` when it is
 * read, so it follows the active language; the English above is only the fallback.
 */
export const REFERRAL_SOURCE_LABELS = {} as Record<ReferralSource, string>;
for (const code of Object.keys(REFERRAL_SOURCE_ENGLISH) as ReferralSource[]) {
  Object.defineProperty(REFERRAL_SOURCE_LABELS, code, {
    enumerable: true,
    get: () => i18n.t(`enums:referralSource.${code}`, { defaultValue: REFERRAL_SOURCE_ENGLISH[code] }),
  });
}

export const referralsModule = {
  record: (referredPatientId: string, referralSource: ReferralSource, referrerPatientId?: string) =>
    fetchApi<PatientReferral>('/api/referrals', {
      method: 'POST',
      body: JSON.stringify({ referredPatientId, referralSource, referrerPatientId }),
    }),

  list: () =>
    fetchApi<PatientReferral[]>('/api/referrals'),

  /** One page of the clinic's referrals, newest first (medopd-docs 112). */
  listPage: (page: number, size: number) =>
    fetchApi<PagedResponse<PatientReferral>>(`/api/referrals?page=${page}&size=${size}`),

  getStats: () =>
    fetchApi<ReferralStats>('/api/referrals/stats'),

  byReferrer: (patientId: string) =>
    fetchApi<PatientReferral[]>(`/api/referrals/patient/${patientId}`),

  applyReward: (id: string, rewardType: RewardType, rewardValue: number) =>
    fetchApi<PatientReferral>(`/api/referrals/${id}/reward`, {
      method: 'POST',
      body: JSON.stringify({ rewardType, rewardValue }),
    }),
};
